import React, { useState, useCallback, useMemo, useEffect } from 'react';
import { useAuthState } from 'react-firebase-hooks/auth';
import { auth } from '../../../firebase';
import { useGoalContext } from '../../../hooks/useGoalContext';
import { Lock, GoalIcon, WarningIcon, AnalyticsIcon, ClipboardIcon, ImageIcon, TipIcon, MilestoneIcon } from '../../icons';
import { 
  Goal, 
  Milestone, 
  Action, 
  GoalType, 
  GoalQuestionnaireAnswers, 
  GoalCommitment,
  GoalBalanceCategory,
  GoalMainCategory,
  ActionPriority,
  ActionType,
  detectActionTypeFromTitle 
} from './types';
import GoalCard from './components/GoalCard';
import AddGoalButton from './components/AddGoalButton';
import CollapsibleSection from './components/CollapsibleSection';
import AddMilestoneModal from './components/AddMilestoneModal';
import AssessmentFlow from './capabilities/AssessmentFlow';
import PhaseCompletionCard from './components/PhaseCompletionCard';
import { GoalPhase } from './components/PhaseIndicator';
import { hasExistingAssessment, getLatestAssessment, getLatestCompleteAssessment, saveCompleteAssessment } from './capabilities/capabilityStorage.service';
import { CAPABILITY_DOMAIN_LABELS } from './capabilities/types';
import { generateComprehensiveActions } from './actionGeneration.service';

// Fase 0: Self Tests (ondernemersprofiel)
import SelfTests from './assessment/SelfTests';
import { SelfTestResults } from './assessment/testDefinitions';
import { hasSelfTestResults, loadSelfTestResults, saveSelfTestResults } from './assessment/selfTestStorage.service';
import { generateSelfTestMilestone } from './assessment/selfTestMilestoneGenerator';
import AssessmentSummaryLoader from './assessment/AssessmentSummaryLoader';

// Fase 1: Intake Flow
import { IntakeFlow } from '../intake';

// Fase 1.5: Pre-sparring context vragen
import PreSparringQuestions from './sparring/PreSparringQuestions';

// Balance & Pattern Detection
import { aggregateBalanceData } from './journey/balanceObservation.service';

// Language Variant
import { loadLanguageVariant } from './shared/languageVariantStorage.service';

// Fase 2: Sparring
import { SparringModeSelector, SparringChat } from './sparring';
import { SparringMode } from './types';

// Fase 3: Overzichten
import { MilestoneOverview } from './overview';
import { GoalTimeline } from './timeline';
import { ChangeLogOverview } from './changelog';
import { VisionboardCollage, VisionboardMini } from './visionboard';
import { MissionVisionView } from './missionvision';
import { getIkigai } from '../purpose/ikigai.service';

// Firebase services
import {
  createGoal,
  createGoalFromIntake,
  getGoals,
  getGoalById,
  updateGoal,
  deleteGoal,
} from './firebase/goals.service';
import {
  createMilestone,
  getAllMilestones,
  updateMilestone,
  deleteMilestone,
} from './firebase/milestones.service';
import {
  createAction,
  getAllActions,
  updateAction,
  deleteAction,
  toggleActionComplete,
} from './firebase/actions.service';
import { getSessionsByGoal, createSparringSession } from './firebase/sparring.service';
import {
  logGoalTitleChanged,
  logGoalStartDateChanged,
  logGoalEndDateChanged,
  logMilestoneCreated,
  logMilestoneTitleChanged,
  logMilestoneDateChanged,
  logMilestoneDeleted,
} from './firebase/changelog.service';
import { createJourneyStart } from './firebase/journey.service';

/**
 * SparringChatWrapper - Loads all entrepreneur context data before rendering chat
 */
interface SparringChatWrapperProps {
  sessionId: string;
  goalId: string;
  ownerId: string;
  milestones: Milestone[];
  actions: Action[];
  selfTestResults: SelfTestResults | null;
  preSparringAnswers: Record<string, string>;
  onMilestoneCreated: (milestoneId: string) => void;
  onSessionClosed: () => void;
  onClose: () => void;
}

const SparringChatWrapper: React.FC<SparringChatWrapperProps> = ({
  sessionId,
  goalId,
  ownerId,
  milestones,
  actions,
  selfTestResults,
  preSparringAnswers,
  onMilestoneCreated,
  onSessionClosed,
  onClose,
}) => {
  const [entrepreneurContext, setEntrepreneurContext] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadFullContext = async () => {
      try {
        // Load capability assessment, goal, and sessions
        const [basicAssessment, completeAssessment, goal, sessions] = await Promise.all([
          getLatestAssessment(ownerId, goalId),
          getLatestCompleteAssessment(ownerId, goalId),
          getGoalById(goalId),
          getSessionsByGoal(ownerId, goalId),
        ]);

        // Extract capability gaps & solutions
        const capabilityGaps = basicAssessment?.decisions
          .filter(d => d.required && d.level === 'basic' && d.resolution === 'learn')
          .map(d => CAPABILITY_DOMAIN_LABELS[d.domain]);

        const capabilitySolutions = basicAssessment?.decisions
          .filter(d => d.required && d.resolution)
          .reduce((acc, d) => ({
            ...acc,
            [CAPABILITY_DOMAIN_LABELS[d.domain]]: 
              d.resolution === 'learn' ? 'Leren' : 
              d.resolution === 'outsource' ? 'Uitbesteden' : 
              d.resolution === 'hire' ? 'Aannemen' : 'Delegeren'
          }), {});

        // Aggregate balance data from goal, milestones, actions, and sessions
        const balanceData = goal ? aggregateBalanceData(goal, milestones, actions, sessions) : null;

        // Load language variant preference
        const languageVariant = loadLanguageVariant();

        // Build full entrepreneur context
        const context = {
          // Self Test Results
          rolePreference: selfTestResults?.rolePreference,
          knowledgeLevels: selfTestResults?.knowledgeLevels,
          
          // Capability Assessment
          capabilityGaps,
          capabilitySolutions,
          
          // DISC & Workstyle Profiles
          discProfile: completeAssessment?.discProfile ? {
            primary: completeAssessment.discProfile.primary,
            secondary: completeAssessment.discProfile.secondary,
            strengths: completeAssessment.discProfile.entrepreneurialStrengths,
            weaknesses: completeAssessment.discProfile.entrepreneurialWeaknesses,
          } : undefined,
          workstyleProfile: completeAssessment?.workstyleProfile ? {
            scores: completeAssessment.workstyleProfile.scores,
            primaryStrength: completeAssessment.workstyleProfile.primaryStrength,
          } : undefined,
          assessmentLevel: completeAssessment?.assessmentLevel,
          
          // Entrepreneur Balance Patterns
          patterns: balanceData ? {
            focusDirection: balanceData.focusDirection, // 'intern' | 'extern' | 'balanced'
            processApproach: balanceData.processApproach, // 'plannen' | 'afronding' | 'balanced'
          } : undefined,
          
          // Pre-sparring answers
          preSparringAnswers,
          
          // Language preference
          languageVariant,
        };

        console.log('🎯 Full Entrepreneur Context loaded:', context);
        setEntrepreneurContext(context);
      } catch (err) {
        console.error('Failed to load entrepreneur context:', err);
        // Fallback to minimal context
        setEntrepreneurContext({
          rolePreference: selfTestResults?.rolePreference,
          knowledgeLevels: selfTestResults?.knowledgeLevels,
          preSparringAnswers,
        });
      } finally {
        setIsLoading(false);
      }
    };

    loadFullContext();
  }, [goalId, ownerId, selfTestResults, preSparringAnswers, milestones, actions]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin inline-block w-8 h-8 border-4 border-green-500 border-t-transparent rounded-full mb-4"></div>
          <p className="text-sm text-gray-600">Context laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <button
        onClick={onClose}
        className="text-sm text-gray-500 hover:text-gray-700"
      >
        &larr; Terug naar overzicht
      </button>
      <SparringChat
        sessionId={sessionId}
        goalId={goalId}
        ownerId={ownerId}
        currentMilestoneCount={milestones.length}
        onMilestoneCreated={onMilestoneCreated}
        onSessionClosed={onSessionClosed}
        onClose={onClose}
        entrepreneurContext={entrepreneurContext}
      />
    </div>
  );
};

/**
 * DoelstellingenPage (Firebase-connected)
 * 
 * A calm, strategic overview of goals with Firebase persistence.
 * 
 * Key UX principles:
 * - Default state = read mode (no visible inputs)
 * - Editing is explicit, inline, and temporary
 * - No form pressure, no planning interference
 * - Collapsible hierarchy for overview
 * - Optimistic UI: updates appear immediately, sync to Firebase in background
 * 
 * Structure:
 * - Hoofddoelen (strategic goals) → GoalCard
 * - Mijlpalen (milestones) → MilestoneRow (nested)
 * - Acties (actions) → ActionRow (nested)
 */

interface GeneratedActionManual {
  title: string;
  priority: ActionPriority;
  actionType: ActionType;
}

/**
 * Generate a concrete action for a manually added milestone
 * NOT a copy of the milestone title
 * NOW WITH PRIORITY AND ACTION TYPE
 */
function generateActionForManualMilestone(milestoneTitle: string): GeneratedActionManual {
  // Extract core topic from title
  const prefixesToRemove = ['Start:', 'Werk aan:', 'Begin met', 'Focus op'];
  let coreTopic = milestoneTitle;
  for (const prefix of prefixesToRemove) {
    if (coreTopic.toLowerCase().startsWith(prefix.toLowerCase())) {
      coreTopic = coreTopic.substring(prefix.length).trim();
    }
  }
  if (coreTopic.length > 40) {
    coreTopic = coreTopic.substring(0, 40).trim();
  }
  
  // Action templates with types
  const actionTemplates: { prefix: string; actionType: ActionType }[] = [
    { prefix: 'Bepaal de eerste stappen voor', actionType: 'plan' },
    { prefix: 'Maak een plan voor', actionType: 'plan' }, 
    { prefix: 'Onderzoek de aanpak voor', actionType: 'research' },
    { prefix: 'Start met voorbereidingen voor', actionType: 'build' },
  ];
  
  const template = actionTemplates[Math.floor(Math.random() * actionTemplates.length)];
  
  return {
    title: `${template.prefix} ${coreTopic}`,
    priority: 'medium', // Manual milestones start with medium priority
    actionType: template.actionType,
  };
}

const DoelstellingenPage: React.FC = () => {
  // ============================================================================
  // AUTH & STATE
  // ============================================================================
  
  const [user] = useAuthState(auth);
  const userId = user?.uid;
  
  // Goal context for team collaboration
  const { activeGoalId, isInTeamContext } = useGoalContext(userId);

  const [goals, setGoals] = useState<Goal[]>([]);
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [actions, setActions] = useState<Action[]>([]);
  const [showCapabilityWizard, setShowCapabilityWizard] = useState(false);
  const [capabilityWizardGoalId, setCapabilityWizardGoalId] = useState<string | null>(null);
  const [pendingSparringGoalAfterWizard, setPendingSparringGoalAfterWizard] = useState<string | null>(null);
  const [isCheckingCapabilities, setIsCheckingCapabilities] = useState(false);
  
  // Phase tracking: map goalId -> { hasCapability: boolean, hasSparring: boolean }
  const [goalPhaseData, setGoalPhaseData] = useState<Map<string, { hasCapability: boolean; hasSparring: boolean }>>(new Map());
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fase 0: Self Tests (ondernemersprofiel)
  const [showSelfTests, setShowSelfTests] = useState(false);
  const [selfTestResults, setSelfTestResults] = useState<SelfTestResults | null>(null);
  const [pendingGoalAfterSelfTests, setPendingGoalAfterSelfTests] = useState<string | null>(null);

  // Purpose / Ikigai summary banner
  const [purposeSummary, setPurposeSummary] = useState<string | null>(null);

  // Fase 1: Intake flow state
  const [showIntakeFlow, setShowIntakeFlow] = useState(false);
  const [journeyRefreshKey, setJourneyRefreshKey] = useState(0);

  // Fase 1.5: Pre-sparring context
  const [showPreSparringQuestions, setShowPreSparringQuestions] = useState(false);
  const [preSparringGoalId, setPreSparringGoalId] = useState<string | null>(null);
  const [preSparringAnswers, setPreSparringAnswers] = useState<Record<string, string>>({});

  // Phase completion card state
  const [completedPhase, setCompletedPhase] = useState<GoalPhase | null>(null);
  const [completedPhaseGoalId, setCompletedPhaseGoalId] = useState<string | null>(null);

  // Fase 2: Sparring state
  const [activeSparringGoalId, setActiveSparringGoalId] = useState<string | null>(null);
  const [activeSparringSessionId, setActiveSparringSessionId] = useState<string | null>(null);
  const [activeSparringMode, setActiveSparringMode] = useState<SparringMode | null>(null);

  // Milestone modal state
  const [showAddMilestoneModal, setShowAddMilestoneModal] = useState(false);
  const [addMilestoneGoalId, setAddMilestoneGoalId] = useState<string | null>(null);

  // Assessment summary modal state
  const [showAssessmentSummary, setShowAssessmentSummary] = useState(false);
  const [assessmentSummaryGoalId, setAssessmentSummaryGoalId] = useState<string | null>(null);

  // ============================================================================
  // DATA LOADING
  // ============================================================================

  // Load phase data for a specific goal
  const loadGoalPhaseData = useCallback(async (goalId: string) => {
    if (!userId) return;
    
    try {
      const [hasCapability, sessions] = await Promise.all([
        hasExistingAssessment(userId, goalId),
        getSessionsByGoal(userId, goalId),
      ]);
      
      setGoalPhaseData(prev => {
        const newMap = new Map(prev);
        newMap.set(goalId, {
          hasCapability,
          hasSparring: sessions.length > 0,
        });
        return newMap;
      });
    } catch (err) {
      console.error('Failed to load phase data for goal:', goalId, err);
    }
  }, [userId]);

  const loadAllData = useCallback(async () => {
    if (!userId) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      // TODO: Implement goal context filtering
      // When activeGoalId is set (team context), only load data for that specific goal:
      // - Filter goals to only show the active goal
      // - Filter milestones and actions to only show those belonging to the active goal
      // This will require modifying the queries or adding post-query filtering
      
      const [goalsData, milestonesData, actionsData, ikigaiData] = await Promise.all([
        getGoals(userId),
        getAllMilestones(userId),
        getAllActions(userId),
        getIkigai(userId),
      ]);
      
      setGoals(goalsData);
      setMilestones(milestonesData);
      setActions(actionsData);

      if (ikigaiData?.purposeSummary) {
        setPurposeSummary(ikigaiData.purposeSummary);
      }

      // Load phase data for all goals
      for (const goal of goalsData) {
        await loadGoalPhaseData(goal.id);
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      setError('Kon doelstellingen niet laden');
    } finally {
      setIsLoading(false);
    }
  }, [userId, loadGoalPhaseData]);

  // Load data on mount and when userId changes
  useEffect(() => {
    if (userId) {
      loadAllData();
    }
  }, [userId, loadAllData]);

  // Load self-test results on mount
  useEffect(() => {
    const storedResults = loadSelfTestResults();
    if (storedResults) {
      setSelfTestResults(storedResults);
    }
  }, []);

  // ============================================================================
  // SELF-TEST HANDLERS (Fase 0)
  // ============================================================================

  const handleSelfTestsComplete = useCallback(async (results: SelfTestResults) => {
    if (!userId) return;
    
    // Save self test results
    saveSelfTestResults(results);
    setSelfTestResults(results);
    setShowSelfTests(false);
    
    const goalId = pendingGoalAfterSelfTests;
    
    // Generate and save milestone from self test results
    if (goalId) {
      try {
        // Generate milestone with actions
        const selfTestMilestone = generateSelfTestMilestone(results);
        
        // Determine target date (3 months from now)
        const today = new Date();
        const targetDate = new Date(today);
        targetDate.setMonth(targetDate.getMonth() + 3);
        
        // Create milestone
        const milestoneId = await createMilestone(userId, goalId, {
          title: selfTestMilestone.title,
          targetDate: targetDate.toISOString().split('T')[0],
          category: selfTestMilestone.category,
          origin: selfTestMilestone.origin,
          fromSparring: false,
        });
        
        // Create actions
        for (const action of selfTestMilestone.actions) {
          if (action.enabled) {
            await createAction(userId, goalId, milestoneId, {
              title: action.title,
              completed: false,
              priority: 'medium',
              origin: 'selftest',
            });
          }
        }
        
        console.log('✅ Self Test milestone created:', selfTestMilestone.title);
        
        // Reload data to show new milestone
        await loadAllData();
      } catch (err) {
        console.error('Failed to create self test milestone:', err);
      }
      
      // Show completion card
      setCompletedPhase('profile');
      setCompletedPhaseGoalId(goalId);
      setPendingGoalAfterSelfTests(null);
    }
  }, [pendingGoalAfterSelfTests, userId, loadAllData]);

  const handleSelfTestsCancel = useCallback(() => {
    setShowSelfTests(false);
    setPendingGoalAfterSelfTests(null);
  }, []);

  // ============================================================================
  // PRE-SPARRING HANDLERS (Fase 1.5)
  // ============================================================================

  const handlePreSparringComplete = useCallback((answers: Record<string, string>) => {
    setPreSparringAnswers(answers);
    setShowPreSparringQuestions(false);
    
    // Continue to sparring mode selection
    if (preSparringGoalId) {
      setActiveSparringGoalId(preSparringGoalId);
      setPreSparringGoalId(null);
    }
  }, [preSparringGoalId]);

  const handlePreSparringSkip = useCallback(() => {
    setShowPreSparringQuestions(false);
    
    // Continue to sparring mode selection without answers
    if (preSparringGoalId) {
      setActiveSparringGoalId(preSparringGoalId);
      setPreSparringGoalId(null);
    }
  }, [preSparringGoalId]);

  // ============================================================================
  // GOAL HANDLERS (Optimistic UI)
  // ============================================================================

  // Start the intake flow (Fase 1)
  const handleStartIntake = useCallback(() => {
    setShowIntakeFlow(true);
  }, []);

  // Cancel the intake flow
  const handleCancelIntake = useCallback(() => {
    setShowIntakeFlow(false);
  }, []);

  // Complete the intake flow - create goal with all intake data (Fase 1)
  const handleIntakeComplete = useCallback(async (intakeData: {
    title: string;
    description?: string;
    startDate: string;
    endDate: string;
    goalType: GoalType;
    questionnaireAnswers: GoalQuestionnaireAnswers;
    mission: string;
    vision: string;
    commitment: GoalCommitment;
    balanceCategory: GoalBalanceCategory;
    goalMainCategory?: GoalMainCategory;
  }) => {
    if (!userId) return;

    // Create temporary goal for optimistic UI
    const tempId = `temp_${Date.now()}`;
    const newGoal: Goal = {
      id: tempId,
      title: intakeData.title,
      description: intakeData.description,
      startDate: intakeData.startDate,
      endDate: intakeData.endDate,
      createdAt: Date.now(),
      ownerId: userId,
      status: 'committed',
      goalType: intakeData.goalType,
      questionnaireAnswers: intakeData.questionnaireAnswers,
      mission: intakeData.mission,
      vision: intakeData.vision,
      commitment: intakeData.commitment,
      balanceCategory: intakeData.balanceCategory,
      goalMainCategory: intakeData.goalMainCategory,
    };
    
    // Optimistic: add to local state and close intake flow
    setGoals(prev => [...prev, newGoal]);
    setShowIntakeFlow(false);

    let finalGoalId = tempId;

    try {
      // Persist to Firebase via intake-specific function
      const realId = await createGoalFromIntake(userId, intakeData);
      finalGoalId = realId;
      
      // Update with real ID
      setGoals(prev => prev.map(g => 
        g.id === tempId ? { ...g, id: realId } : g
      ));
      
      // Als er een startvideo is, maak journey start entry
      if (intakeData.commitment?.videoUrl) {
        try {
          await createJourneyStart(userId, realId, intakeData.commitment.videoUrl);
          console.log('Journey start created with video');
          // Trigger refresh van tijdlijn
          setJourneyRefreshKey(prev => prev + 1);
        } catch (journeyErr) {
          console.error('Failed to create journey start:', journeyErr);
          // Don't rollback the goal if only the journey entry fails
        }
      }

      // Show completion card for intake phase
      setCompletedPhase('intake');
      setCompletedPhaseGoalId(realId);
    } catch (err) {
      console.error('Failed to create goal from intake:', err);
      // Rollback on error
      setGoals(prev => prev.filter(g => g.id !== tempId));
    }
  }, [userId]);

  // ============================================================================
  // SPARRING HANDLERS (Fase 2)
  // ============================================================================

  // Start sparring for a goal
  const handleStartSparring = useCallback(async (goalId: string) => {
    if (!userId) return;

    setIsCheckingCapabilities(true);
    setError(null);

    try {
      // Stap 1: Check self-test results (ondernemersprofiel)
      if (!hasSelfTestResults()) {
        setPendingGoalAfterSelfTests(goalId);
        setShowSelfTests(true);
        return;
      }

      // Stap 2: Check capability assessment
      const hasAssessment = await hasExistingAssessment(userId, goalId);
      if (!hasAssessment) {
        setCapabilityWizardGoalId(goalId);
        setPendingSparringGoalAfterWizard(goalId);
        setShowCapabilityWizard(true);
        return;
      }

      // Stap 3: Show pre-sparring context questions
      // (Skip if already answered for this goal in this session)
      if (Object.keys(preSparringAnswers).length === 0) {
        setPreSparringGoalId(goalId);
        setShowPreSparringQuestions(true);
        return;
      }

      // Stap 4: Start sparring mode selection
      setActiveSparringGoalId(goalId);
    } catch (err) {
      console.error('Failed to check capabilities:', err);
      setError('Kon capaciteiten niet laden');
    } finally {
      setIsCheckingCapabilities(false);
    }
  }, [userId, preSparringAnswers]);

  // Cancel sparring mode selection and reload data
  const handleCancelSparring = useCallback(() => {
    setActiveSparringGoalId(null);
    setActiveSparringSessionId(null);
    setActiveSparringMode(null);
    // Reload all data to pick up any milestones created during sparring
    loadAllData();
  }, [loadAllData]);

  // Sparring session created - ready for chat
  const handleSparringSessionCreated = useCallback((sessionId: string, mode: SparringMode) => {
    setActiveSparringSessionId(sessionId);
    setActiveSparringMode(mode);
    // TODO: Navigate to SparringChat component (Fase 2.4)
    console.log('Sparring session created:', { sessionId, mode });
  }, []);

  const handleCapabilityWizardComplete = useCallback(() => {
    setShowCapabilityWizard(false);
    const completedGoalId = capabilityWizardGoalId || pendingSparringGoalAfterWizard;
    setCapabilityWizardGoalId(null);
    loadAllData();
    
    // Reload phase data for this specific goal
    if (completedGoalId) {
      loadGoalPhaseData(completedGoalId);
    }
    
    // ALWAYS show completion card after capability assessment
    // (regardless of how the wizard was opened)
    if (completedGoalId) {
      setCompletedPhase('capability');
      setCompletedPhaseGoalId(completedGoalId);
      setPendingSparringGoalAfterWizard(null);
    }
  }, [loadAllData, loadGoalPhaseData, capabilityWizardGoalId, pendingSparringGoalAfterWizard]);

  const handleCapabilityWizardCancel = useCallback(() => {
    setShowCapabilityWizard(false);
    setCapabilityWizardGoalId(null);
    setPendingSparringGoalAfterWizard(null);
  }, []);

  // ============================================================================
  // PHASE COMPLETION CARD HANDLERS
  // ============================================================================

  const handlePhaseCompletionContinue = useCallback(() => {
    const goalId = completedPhaseGoalId;
    const phase = completedPhase;
    
    // Clear the completion card state
    setCompletedPhase(null);
    setCompletedPhaseGoalId(null);

    if (!goalId) return;

    // Navigate to the next phase
    if (phase === 'intake') {
      // After intake → Go to Profile (SelfTests)
      setPendingGoalAfterSelfTests(goalId);
      setShowSelfTests(true);
    } else if (phase === 'profile') {
      // After profile → Go to Skills (capability wizard)
      setCapabilityWizardGoalId(goalId);
      setShowCapabilityWizard(true);
      setPendingSparringGoalAfterWizard(goalId);
    } else if (phase === 'capability') {
      // After skills → Go to Sparring (check pre-sparring first)
      if (Object.keys(preSparringAnswers).length === 0) {
        setPreSparringGoalId(goalId);
        setShowPreSparringQuestions(true);
      } else {
        setActiveSparringGoalId(goalId);
      }
    }
    // Note: sparring and milestones already have their own completion cards
  }, [completedPhase, completedPhaseGoalId, preSparringAnswers]);

  const handlePhaseCompletionDismiss = useCallback(() => {
    setCompletedPhase(null);
    setCompletedPhaseGoalId(null);
  }, []);

  // Legacy: Quick add goal (bypasses intake - kept for backwards compatibility)
  const handleAddGoal = useCallback(async (title: string, isTeamGoal?: boolean) => {
    if (!userId) return;

    // Create temporary goal for optimistic UI
    const tempId = `temp_${Date.now()}`;
    const newGoal: Goal = {
      id: tempId,
      title,
      startDate: '',
      endDate: '',
      createdAt: Date.now(),
      ownerId: userId,
      status: 'draft', // Draft status when bypassing intake
      isTeamGoal: isTeamGoal || false,
      // For team goals, add owner as first team member
      teamMembers: isTeamGoal ? [{
        userId,
        userName: user?.displayName || 'Jij',
        userEmail: user?.email || '',
        userPhotoURL: user?.photoURL || undefined,
        role: 'owner' as const,
        accessLevel: 'all' as const,
        color: '#4F6660', // Brand orange
        joinedAt: Date.now(),
        invitedBy: userId,
      }] : undefined,
    };
    
    // Optimistic: add to local state
    setGoals(prev => [...prev, newGoal]);

    try {
      // Persist to Firebase
      const realId = await createGoal(userId, {
        title,
        startDate: '',
        endDate: '',
        status: 'draft',
        isTeamGoal: isTeamGoal || false,
        teamMembers: isTeamGoal ? [{
          userId,
          userName: user?.displayName || 'Jij',
          userEmail: user?.email || '',
          userPhotoURL: user?.photoURL || undefined,
          role: 'owner' as const,
          accessLevel: 'all' as const,
          color: '#4F6660',
          joinedAt: Date.now(),
          invitedBy: userId,
        }] : undefined,
      });
      
      // Update with real ID
      setGoals(prev => prev.map(g => 
        g.id === tempId ? { ...g, id: realId } : g
      ));
      
      // For team goals, skip intake and go directly to project sparring
      if (isTeamGoal) {
        // Set goal as committed (skip intake flow)
        await updateGoal(realId, { status: 'committed' });
        setGoals(prev => prev.map(g => 
          g.id === realId ? { ...g, status: 'committed' } : g
        ));
        
        // Create project sparring session directly
        try {
          const sessionId = await createSparringSession(userId, realId, 'project');
          setActiveSparringGoalId(realId);
          setActiveSparringSessionId(sessionId);
          setActiveSparringMode('project');
        } catch (err) {
          console.error('Failed to create project sparring session:', err);
        }
      }
    } catch (err) {
      console.error('Failed to create goal:', err);
      // Rollback on error
      setGoals(prev => prev.filter(g => g.id !== tempId));
    }
  }, [userId, user]);

  const handleUpdateGoal = useCallback(async (id: string, updates: Partial<Goal>) => {
    if (!userId) return;
    
    // Get current goal for comparison (for changelog)
    const currentGoal = goals.find(g => g.id === id);
    
    // Optimistic: update local state first
    setGoals(prev => prev.map(g => 
      g.id === id ? { ...g, ...updates } : g
    ));
    
    try {
      await updateGoal(id, updates);
      
      // Log changes to changelog
      if (currentGoal) {
        if (updates.title && updates.title !== currentGoal.title) {
          await logGoalTitleChanged(userId, id, currentGoal.title, updates.title);
        }
        if (updates.startDate && updates.startDate !== currentGoal.startDate) {
          await logGoalStartDateChanged(userId, id, currentGoal.startDate || '', updates.startDate);
        }
        if (updates.endDate && updates.endDate !== currentGoal.endDate) {
          await logGoalEndDateChanged(userId, id, currentGoal.endDate || '', updates.endDate);
        }
      }
    } catch (err) {
      console.error('Failed to update goal:', err);
      // Rollback on error
      await loadAllData();
    }
  }, [userId, goals, loadAllData]);

  const handleDeleteGoal = useCallback(async (id: string) => {
    // Confirm before delete
    if (!window.confirm('Weet je zeker dat je dit hoofddoel wilt verwijderen? Alle bijbehorende mijlpalen en acties worden ook verwijderd.')) {
      return;
    }
    
    // Store for rollback
    const goalToDelete = goals.find(g => g.id === id);
    const milestonesToDelete = milestones.filter(m => m.goalId === id);
    const actionsToDelete = actions.filter(a => a.goalId === id);
    
    // Optimistic: remove from local state
    setGoals(prev => prev.filter(g => g.id !== id));
    setMilestones(prev => prev.filter(m => m.goalId !== id));
    setActions(prev => prev.filter(a => a.goalId !== id));
    
    try {
      await deleteGoal(id);
    } catch (err) {
      console.error('Failed to delete goal:', err);
      // Rollback on error
      if (goalToDelete) {
        setGoals(prev => [...prev, goalToDelete]);
        setMilestones(prev => [...prev, ...milestonesToDelete]);
        setActions(prev => [...prev, ...actionsToDelete]);
      }
    }
  }, [goals, milestones, actions]);

  // ============================================================================
  // MILESTONE HANDLERS (Optimistic UI)
  // ============================================================================

  // Open modal voor nieuwe mijlpaal
  const handleAddMilestone = useCallback((goalId: string) => {
    setAddMilestoneGoalId(goalId);
    setShowAddMilestoneModal(true);
  }, []);

  // Daadwerkelijk opslaan van mijlpaal (vanuit modal)
  const handleSaveMilestone = useCallback(async (data: { title: string; targetDate: string; globalPeriod?: string }) => {
    if (!userId || !addMilestoneGoalId) return;

    const goalId = addMilestoneGoalId;
    const tempId = `temp_${Date.now()}`;
    const newMilestone: Milestone = {
      id: tempId,
      title: data.title,
      targetDate: data.targetDate,
      globalPeriod: data.globalPeriod,
      goalId,
      createdAt: Date.now(),
      ownerId: userId,
      origin: 'manual',
    };
    
    // Close modal
    setShowAddMilestoneModal(false);
    setAddMilestoneGoalId(null);
    
    // Optimistic: add to local state
    setMilestones(prev => [...prev, newMilestone]);

    try {
      const realId = await createMilestone(userId, goalId, {
        title: data.title,
        targetDate: data.targetDate,
        globalPeriod: data.globalPeriod,
        origin: 'manual',
      });
      
      // Update with real ID
      setMilestones(prev => prev.map(m => 
        m.id === tempId ? { ...m, id: realId } : m
      ));
      
      // Generate comprehensive actions using ALL available data
      try {
        // Load assessment and context
        const [completeAssessment, goalObj] = await Promise.all([
          getLatestCompleteAssessment(userId, goalId),
          goals.find(g => g.id === goalId),
        ]);
        
        if (goalObj) {
          // Build milestone object
          const milestoneObj: Milestone = {
            id: realId,
            ownerId: userId,
            goalId,
            title: data.title,
            targetDate: data.targetDate,
            createdAt: Date.now(),
            globalPeriod: data.globalPeriod,
            origin: 'manual',
          };
          
          // Build comprehensive context
          const actionContext = {
            goal: goalObj,
            milestone: milestoneObj,
            completeAssessment: completeAssessment || undefined,
            extendedDecisions: completeAssessment?.extendedDecisions,
            sparringMessages: [], // Manual milestones don't have sparring context
            userResponses: [],
            selfTestResults: selfTestResults || undefined,
            preSparringAnswers: preSparringAnswers,
            entrepreneurContext: undefined,
          };
          
          // Generate comprehensive actions (for ALL domains)
          const generatedActions = await generateComprehensiveActions(actionContext);
          
          console.log(`✅ Generated ${generatedActions.length} actions for manual milestone: ${data.title}`);
          
          // Save all actions
          const actionPromises = generatedActions.map(async (action) => {
            try {
              const actionId = await createAction(userId, goalId, realId, {
                title: action.title,
                completed: false,
                origin: 'milestone_generated',
                priority: action.priority,
                actionType: action.actionType,
                domain: action.domain, // Include domain tag
              });
              
              // Add to local state
              const newAction: Action = {
                id: actionId,
                title: action.title,
                completed: false,
                milestoneId: realId,
                goalId,
                createdAt: Date.now(),
                ownerId: userId,
                origin: 'milestone_generated',
                priority: action.priority,
                actionType: action.actionType,
                domain: action.domain,
              };
              
              setActions(prev => [...prev, newAction]);
              return actionId;
            } catch (err) {
              console.warn('Failed to create individual action:', err);
              return null;
            }
          });
          
          await Promise.all(actionPromises);
          
          // Notify AgendaPage about new actions
          window.dispatchEvent(new CustomEvent('doelstellingen-actions-changed'));
        } else {
          // Fallback: use simple action generation if goal not found
          const generatedAction = generateActionForManualMilestone(data.title);
          const actionId = await createAction(userId, goalId, realId, {
            title: generatedAction.title,
            completed: false,
            origin: 'milestone_generated',
            priority: generatedAction.priority,
            actionType: generatedAction.actionType,
          });
          
          const newAction: Action = {
            id: actionId,
            title: generatedAction.title,
            completed: false,
            milestoneId: realId,
            goalId,
            createdAt: Date.now(),
            ownerId: userId,
            origin: 'milestone_generated',
            priority: generatedAction.priority,
            actionType: generatedAction.actionType,
          };
          
          setActions(prev => [...prev, newAction]);
          window.dispatchEvent(new CustomEvent('doelstellingen-actions-changed'));
        }
      } catch (actionErr) {
        console.error('Could not generate comprehensive actions, using fallback:', actionErr);
        // Fallback to simple action generation
        try {
          const generatedAction = generateActionForManualMilestone(data.title);
          const actionId = await createAction(userId, goalId, realId, {
            title: generatedAction.title,
            completed: false,
            origin: 'milestone_generated',
            priority: generatedAction.priority,
            actionType: generatedAction.actionType,
          });
          
          const newAction: Action = {
            id: actionId,
            title: generatedAction.title,
            completed: false,
            milestoneId: realId,
            goalId,
            createdAt: Date.now(),
            ownerId: userId,
            origin: 'milestone_generated',
            priority: generatedAction.priority,
            actionType: generatedAction.actionType,
          };
          
          setActions(prev => [...prev, newAction]);
          window.dispatchEvent(new CustomEvent('doelstellingen-actions-changed'));
        } catch (fallbackErr) {
          console.warn('Could not create fallback action:', fallbackErr);
        }
      }
      
      // Log milestone creation
      await logMilestoneCreated(userId, goalId, realId, data.title);
    } catch (err) {
      console.error('Failed to create milestone:', err);
      // Rollback on error
      setMilestones(prev => prev.filter(m => m.id !== tempId));
    }
  }, [userId, addMilestoneGoalId]);

  const handleUpdateMilestone = useCallback(async (id: string, updates: Partial<Milestone>) => {
    if (!userId) return;
    
    // Get current milestone for comparison (for changelog)
    const currentMilestone = milestones.find(m => m.id === id);
    
    // Optimistic: update local state first
    setMilestones(prev => prev.map(m => 
      m.id === id ? { ...m, ...updates } : m
    ));
    
    try {
      await updateMilestone(id, updates);
      
      // Log changes to changelog
      if (currentMilestone) {
        if (updates.title && updates.title !== currentMilestone.title) {
          await logMilestoneTitleChanged(
            userId, 
            currentMilestone.goalId, 
            id, 
            currentMilestone.title, 
            updates.title
          );
        }
        if (updates.targetDate && updates.targetDate !== currentMilestone.targetDate) {
          await logMilestoneDateChanged(
            userId, 
            currentMilestone.goalId, 
            id, 
            currentMilestone.title,
            currentMilestone.targetDate || '', 
            updates.targetDate
          );
        }
      }
    } catch (err) {
      console.error('Failed to update milestone:', err);
      await loadAllData();
    }
  }, [userId, milestones, loadAllData]);

  const handleDeleteMilestone = useCallback(async (id: string) => {
    if (!userId) return;
    
    // Store for rollback
    const milestoneToDelete = milestones.find(m => m.id === id);
    const actionsToDelete = actions.filter(a => a.milestoneId === id);
    
    // Optimistic: remove from local state
    setMilestones(prev => prev.filter(m => m.id !== id));
    setActions(prev => prev.filter(a => a.milestoneId !== id));
    
    try {
      await deleteMilestone(id);
      
      // Log deletion to changelog
      if (milestoneToDelete) {
        await logMilestoneDeleted(
          userId, 
          milestoneToDelete.goalId, 
          id, 
          milestoneToDelete.title
        );
      }
    } catch (err) {
      console.error('Failed to delete milestone:', err);
      // Rollback on error
      if (milestoneToDelete) {
        setMilestones(prev => [...prev, milestoneToDelete]);
        setActions(prev => [...prev, ...actionsToDelete]);
      }
    }
  }, [userId, milestones, actions]);

  // ============================================================================
  // ACTION HANDLERS (Optimistic UI)
  // ============================================================================

  const handleAddAction = useCallback(async (milestoneId: string, goalId: string) => {
    if (!userId) return;

    const tempId = `temp_${Date.now()}`;
    const newAction: Action = {
      id: tempId,
      title: 'Nieuwe actie',
      milestoneId,
      goalId,
      completed: false,
      createdAt: Date.now(),
      ownerId: userId,
    };
    
    // Optimistic: add to local state
    setActions(prev => [...prev, newAction]);

    try {
      const realId = await createAction(userId, goalId, milestoneId, {
        title: 'Nieuwe actie',
        completed: false,
      });
      
      // Update with real ID
      setActions(prev => prev.map(a => 
        a.id === tempId ? { ...a, id: realId } : a
      ));
    } catch (err) {
      console.error('Failed to create action:', err);
      // Rollback on error
      setActions(prev => prev.filter(a => a.id !== tempId));
    }
  }, [userId]);

  const handleToggleAction = useCallback(async (id: string) => {
    const action = actions.find(a => a.id === id);
    if (!action) return;
    
    const newCompleted = !action.completed;
    
    // Optimistic: update local state first
    setActions(prev => prev.map(a => 
      a.id === id ? { ...a, completed: newCompleted } : a
    ));
    
    try {
      await toggleActionComplete(id, newCompleted);
    } catch (err) {
      console.error('Failed to toggle action:', err);
      // Rollback on error
      setActions(prev => prev.map(a => 
        a.id === id ? { ...a, completed: !newCompleted } : a
      ));
    }
  }, [actions]);

  const handleUpdateAction = useCallback(async (id: string, title: string) => {
    // Optimistic: update local state first
    setActions(prev => prev.map(a => 
      a.id === id ? { ...a, title } : a
    ));
    
    try {
      await updateAction(id, { title });
    } catch (err) {
      console.error('Failed to update action:', err);
      await loadAllData();
    }
  }, [loadAllData]);

  const handleDeleteAction = useCallback(async (id: string) => {
    // Store for rollback
    const actionToDelete = actions.find(a => a.id === id);
    
    // Optimistic: remove from local state
    setActions(prev => prev.filter(a => a.id !== id));
    
    try {
      await deleteAction(id);
    } catch (err) {
      console.error('Failed to delete action:', err);
      // Rollback on error
      if (actionToDelete) {
        setActions(prev => [...prev, actionToDelete]);
      }
    }
  }, [actions]);

  const handlePlanAction = useCallback((action: Action | { id: string; title: string; [key: string]: any }) => {
    // This will open the agenda modal or sidebar
    // For now, just log it
    console.log('Plan action in agenda:', action);
    // TODO: Integrate with agenda modal
    alert(`"${action.title}" plannen in agenda - integratie volgt`);
  }, []);

  // ============================================================================
  // DERIVED DATA
  // ============================================================================

  const getMilestonesForGoal = useCallback((goalId: string) => {
    return milestones.filter(m => m.goalId === goalId);
  }, [milestones]);

  const getActionsForGoal = useCallback((goalId: string) => {
    return actions.filter(a => a.goalId === goalId);
  }, [actions]);

  const capabilityWizardGoal = capabilityWizardGoalId
    ? goals.find(g => g.id === capabilityWizardGoalId)
    : null;

  // ============================================================================
  // RENDER
  // ============================================================================

  // Not logged in
  if (!user) {
    return (
      <div className="w-full text-center py-12">
        <Lock size={32} className="text-gray-500 mb-4 mx-auto block" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Log in om je doelstellingen te beheren
        </h3>
        <p className="text-sm text-gray-500">
          Je moet ingelogd zijn om doelstellingen aan te maken en te bekijken.
        </p>
      </div>
    );
  }

  // Loading state (only on initial load) — full-width skeleton to prevent visual narrow flash
  if (isLoading) {
    return (
      <div className="w-full space-y-3">
        {/* Skeleton GoalCard */}
        <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse">
          <div className="bg-gray-100 px-5 py-3">
            <div className="flex items-center gap-2">
              <div className="h-3 bg-gray-200 rounded w-16"></div>
              <div className="h-3 bg-gray-200 rounded w-12"></div>
              <div className="h-3 bg-gray-200 rounded w-14"></div>
              <div className="h-3 bg-gray-200 rounded w-10"></div>
            </div>
            <div className="mt-2 h-1.5 bg-gray-200 rounded-full w-full"></div>
          </div>
          <div className="px-5 py-4 space-y-3">
            <div className="h-5 bg-gray-200 rounded w-3/4"></div>
            <div className="h-3 bg-gray-100 rounded w-1/2"></div>
            <div className="space-y-2 mt-3">
              <div className="h-8 bg-gray-50 rounded w-full"></div>
              <div className="h-8 bg-gray-50 rounded w-full"></div>
              <div className="h-8 bg-gray-50 rounded w-full"></div>
            </div>
          </div>
        </div>
        {/* Skeleton Tijdlijn */}
        <div className="w-full bg-white rounded-lg border border-gray-200 overflow-hidden animate-pulse">
          <div className="px-4 py-3 flex items-center gap-2">
            <div className="h-4 w-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-20"></div>
          </div>
        </div>
        <p className="text-xs text-center" style={{ color: '#8DA8A0' }}>Doelstellingen laden...</p>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <div className="w-full text-center py-12">
        <WarningIcon size={32} className="text-amber-500 mb-4 mx-auto block" />
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          Er ging iets mis
        </h3>
        <p className="text-sm text-gray-500 mb-4">{error}</p>
        <button
          onClick={loadAllData}
          className="text-orange-600 hover:text-orange-700 text-sm font-medium"
        >
          Opnieuw proberen
        </button>
      </div>
    );
  }

  // Show Intake Flow (Fase 1)
  if (showIntakeFlow) {
    return (
      <div className="w-full bg-white rounded-lg shadow-sm border border-gray-200">
        <IntakeFlow
          userId={userId!}
          onComplete={handleIntakeComplete}
          onCancel={handleCancelIntake}
        />
      </div>
    );
  }

  // Show Sparring Mode Selector (Fase 2)
  if (activeSparringGoalId && !activeSparringSessionId) {
    const sparringGoal = goals.find(g => g.id === activeSparringGoalId);
    return (
      <div className="w-full space-y-4">
        <button
          onClick={handleCancelSparring}
          className="text-sm text-gray-500 hover:text-gray-700"
        >
          &larr; Terug naar overzicht
        </button>
        {sparringGoal && (
          <div className="mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Sparring voor: {sparringGoal.title}
            </h2>
          </div>
        )}
        <SparringModeSelector
          goalId={activeSparringGoalId}
          ownerId={userId!}
          onSessionCreated={handleSparringSessionCreated}
        />
      </div>
    );
  }

  // Show Sparring Chat (Fase 2.4/2.6)
  if (activeSparringSessionId && activeSparringGoalId) {
    const milestonesForGoal = getMilestonesForGoal(activeSparringGoalId);
    const actionsForGoal = getActionsForGoal(activeSparringGoalId);
    
    return (
      <SparringChatWrapper
        sessionId={activeSparringSessionId}
        goalId={activeSparringGoalId}
        ownerId={userId!}
        milestones={milestonesForGoal}
        actions={actionsForGoal}
        selfTestResults={selfTestResults}
        preSparringAnswers={preSparringAnswers}
        // ⚓ ANCHORED: 2026-01-27 - NIET WIJZIGEN!
        // loadAllData() hier aanroepen veroorzaakt state machine reset bug.
        // Zie: PROJECT_NOTES/DOELSTELLINGEN_FIXES_2026-01-27.md
        onMilestoneCreated={(milestoneId: string) => {
          // Just log - don't reload data during saving to prevent re-renders
          // Data will be reloaded when session closes via onSessionClosed
          console.log('Milestone created from sparring:', milestoneId);
        }}
        onSessionClosed={() => {
          console.log('Sparring session closed, reloading data...');
          handleCancelSparring();
          loadAllData();
        }}
        onClose={handleCancelSparring}
      />
    );
  }

  return (
    <div className="w-full space-y-3 pb-8">
      {/* Purpose summary banner - shown when Ikigai is filled in */}
      {purposeSummary && (
        <div
          className="rounded-lg border px-4 py-3 flex items-center gap-3"
          style={{ backgroundColor: 'rgba(122,148,136,0.07)', borderColor: 'rgba(122,148,136,0.25)' }}
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0, color: '#5E7E74' }}>
            <circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.2" />
            <circle cx="5.5" cy="6.5" r="2.5" stroke="currentColor" strokeWidth="0.9" strokeOpacity="0.5" />
            <circle cx="10.5" cy="6.5" r="2.5" stroke="currentColor" strokeWidth="0.9" strokeOpacity="0.5" />
            <circle cx="5.5" cy="9.5" r="2.5" stroke="currentColor" strokeWidth="0.9" strokeOpacity="0.5" />
            <circle cx="10.5" cy="9.5" r="2.5" stroke="currentColor" strokeWidth="0.9" strokeOpacity="0.5" />
          </svg>
          <div className="flex-1 min-w-0">
            <span className="text-xs font-medium" style={{ color: '#3C4F49' }}>
              Purpose:{' '}
            </span>
            <span className="text-xs text-gray-600 truncate">{purposeSummary}</span>
          </div>
        </div>
      )}

      {/* Goals list */}
      <div className="space-y-3">
        {goals.length === 0 ? (
          <div className="w-full text-center py-8">
            <div className="inline-block rounded-full p-4 mb-3" style={{ backgroundColor: 'rgba(141,168,160,0.1)' }}>
              <GoalIcon size={28} style={{ color: '#8DA8A0' }} className="mx-auto block" />
            </div>
            <h3 className="text-sm font-semibold mb-1" style={{ color: '#3C4F49' }}>
              Nog geen hoofddoelen
            </h3>
            <p className="text-xs mb-4" style={{ color: '#5E6E68' }}>
              Definieer je strategische doelen om je acties richting te geven
            </p>
            {/* Primary CTA: Start intake flow */}
            <button
              onClick={handleStartIntake}
              className="px-6 py-3 text-white rounded-lg font-medium transition-colors"
              style={{ backgroundColor: '#8DA8A0' }}
              onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = '#7A9488'; }}
              onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = '#8DA8A0'; }}
            >
              Nieuw doel starten
            </button>
          </div>
        ) : (
          goals.map(goal => (
            <div key={goal.id} className="space-y-2">
              {/* Visionboard Mini - horizontal thumbnails above goal */}
              {goal.status === 'committed' && userId && (
                <>
                  {console.log('[DoelstellingenPage] Rendering VisionboardMini for goal:', goal.id, 'userId:', userId, 'status:', goal.status)}
                  <VisionboardMini
                    goalId={goal.id}
                    ownerId={userId}
                    onExpand={() => {
                      // Scroll naar visionboard collapsible sectie
                      const visionboardSection = document.getElementById(`visionboard-${goal.id}`);
                      if (visionboardSection) {
                        visionboardSection.scrollIntoView({ behavior: 'smooth', block: 'center' });
                        // Open de sectie (click op header)
                        const header = visionboardSection.querySelector('button');
                        if (header) header.click();
                      }
                    }}
                  />
                </>
              )}
              
              {/* Assessment Summary Button - Show if assessment exists */}
              {goalPhaseData.get(goal.id)?.hasCapability && (
                <button
                  onClick={() => {
                    setAssessmentSummaryGoalId(goal.id);
                    setShowAssessmentSummary(true);
                  }}
                  className="w-full px-4 py-2 bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200 rounded-lg hover:from-blue-100 hover:to-purple-100 transition-colors text-sm font-medium text-blue-900 flex items-center justify-center gap-2"
                >
                  <AnalyticsIcon size={16} className="inline text-blue-700" /> Bekijk je profiel samenvatting
                </button>
              )}

              {/* Mission & Vision - always on top as reminder */}
              {goal.status === 'committed' && (goal.mission || goal.vision) && (
                <div className="mt-4">
                  <CollapsibleSection 
                    title="Missie & Visie" 
                    icon={<GoalIcon size={16} />} 
                    defaultExpanded={false}
                  >
                    <MissionVisionView
                      goalId={goal.id}
                      mission={goal.mission}
                      vision={goal.vision}
                      onUpdate={async (mission, vision) => {
                        await handleUpdateGoal(goal.id, { mission, vision });
                      }}
                    />
                  </CollapsibleSection>
                </div>
              )}
              
              <GoalCard
                goal={goal}
                milestones={getMilestonesForGoal(goal.id)}
                actions={getActionsForGoal(goal.id)}
                onUpdateGoal={handleUpdateGoal}
                onUpdateMilestone={handleUpdateMilestone}
                onToggleAction={handleToggleAction}
                onUpdateAction={handleUpdateAction}
                onAddMilestone={handleAddMilestone}
                onAddAction={handleAddAction}
                onDeleteGoal={handleDeleteGoal}
                onDeleteMilestone={handleDeleteMilestone}
                onDeleteAction={handleDeleteAction}
                onPlanAction={handlePlanAction}
                defaultExpanded={false}
                hasSelfTestResults={!!selfTestResults}
                hasCapabilityAssessment={goalPhaseData.get(goal.id)?.hasCapability ?? false}
                hasSparringSession={goalPhaseData.get(goal.id)?.hasSparring ?? false}
                onPhaseAction={(phase) => {
                  // Handle phase action clicks
                  if (phase === 'profile') {
                    // Open Self Tests (ondernemersprofiel)
                    setPendingGoalAfterSelfTests(goal.id);
                    setShowSelfTests(true);
                  } else if (phase === 'capability') {
                    // Open Assessment Flow (DISC + Werkstijl + Skills)
                    setCapabilityWizardGoalId(goal.id);
                    setShowCapabilityWizard(true);
                  } else if (phase === 'sparring') {
                    handleStartSparring(goal.id);
                  }
                }}
              />
              {/* Goal Timeline - visual timeline with milestone markers */}
              {goal.status === 'committed' && goal.startDate && goal.endDate && (
                <div className="mt-4">
                  <CollapsibleSection 
                    title="Tijdlijn" 
                    icon={<AnalyticsIcon size={16} />} 
                    defaultExpanded={true}
                  >
                    <GoalTimeline
                      goal={goal}
                      ownerId={userId!}
                      milestones={getMilestonesForGoal(goal.id)}
                      onMilestoneUpdated={loadAllData}
                      refreshKey={journeyRefreshKey}
                    />
                  </CollapsibleSection>
                </div>
              )}
              
              {/* 
                VERWIJDERD: Oude "Start sparring" button 
                Nu wordt de gebruiker door de flow geleid via de fase-indicator en completion cards
                Zie: PhaseIndicator.tsx en PhaseCompletionCard.tsx
              */}
              {/* Milestone Overview - only for committed goals with milestones (Fase 3.1) */}
              {goal.status === 'committed' && getMilestonesForGoal(goal.id).length > 0 && (
                <div className="mt-4">
                  <CollapsibleSection 
                    title="Mijlpaal Overzicht" 
                    icon={<MilestoneIcon size={16} />} 
                    defaultExpanded={false}
                  >
                    <MilestoneOverview
                      goalId={goal.id}
                      ownerId={userId!}
                    />
                  </CollapsibleSection>
                </div>
              )}
              {/* Visionboard Collage - visual inspiration images */}
              {goal.status === 'committed' && (
                <div className="mt-4" id={`visionboard-${goal.id}`}>
                  <CollapsibleSection 
                    title="Visionboard" 
                    icon={<ImageIcon size={16} />} 
                    defaultExpanded={false}
                  >
                    <VisionboardCollage
                      goalId={goal.id}
                      ownerId={userId!}
                      commitmentVideoUrl={goal.commitment?.videoUrl}
                    />
                  </CollapsibleSection>
                </div>
              )}
              {/* Change Log Overview - tracks all changes to goals, milestones, videos */}
              {goal.status === 'committed' && (
                <div className="mt-4">
                  <CollapsibleSection 
                    title="Wijzigingslog" 
                    icon={<ClipboardIcon size={16} />} 
                    defaultExpanded={false}
                  >
                    <ChangeLogOverview
                      goalId={goal.id}
                      ownerId={userId!}
                    />
                  </CollapsibleSection>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Add goal button - now triggers intake flow */}
      {goals.length > 0 && (
        <div className="flex justify-center pt-4">
          <button
            onClick={handleStartIntake}
            className="flex items-center gap-2 px-4 py-2 text-orange-600 hover:text-orange-700 hover:bg-orange-50 rounded-lg transition-colors"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuw doel toevoegen
          </button>
        </div>
      )}
      {/* Self Tests Modal (Fase 0 - Ondernemersprofiel) */}
      {showSelfTests && (
        <SelfTests
          onComplete={handleSelfTestsComplete}
          onCancel={handleSelfTestsCancel}
        />
      )}

      {/* Pre-Sparring Questions Modal (Fase 1.5 - Context vragen) */}
      {showPreSparringQuestions && (
        <PreSparringQuestions
          onComplete={handlePreSparringComplete}
          onSkip={handlePreSparringSkip}
        />
      )}

      {/* Assessment Flow Modal (Fase 2 - DISC + Werkstijl + Skills) */}
      {showCapabilityWizard && capabilityWizardGoal && (
        <AssessmentFlow
          goal={capabilityWizardGoal}
          ownerId={userId!}
          onComplete={async (assessment) => {
            console.log('🎯 [DoelstellingenPage] AssessmentFlow onComplete called');
            console.log('📦 [DoelstellingenPage] Assessment data:', {
              decisions: assessment.decisions.length,
              level: assessment.assessmentLevel
            });
            await saveCompleteAssessment(assessment);
            console.log('✅ [DoelstellingenPage] Assessment saved, calling handleCapabilityWizardComplete');
            handleCapabilityWizardComplete();
          }}
          onCancel={handleCapabilityWizardCancel}
        />
      )}

      {/* Add Milestone Modal */}
      {showAddMilestoneModal && addMilestoneGoalId && (() => {
        const goal = goals.find(g => g.id === addMilestoneGoalId);
        if (!goal) return null;
        return (
          <AddMilestoneModal
            goalId={addMilestoneGoalId}
            goalTitle={goal.title}
            goalStartDate={goal.startDate}
            goalEndDate={goal.endDate}
            onSave={handleSaveMilestone}
            onCancel={() => {
              setShowAddMilestoneModal(false);
              setAddMilestoneGoalId(null);
            }}
          />
        );
      })()}

      {/* Phase Completion Card - Shows when a phase is just completed */}
      {completedPhase && (
        <PhaseCompletionCard
          completedPhase={completedPhase}
          onContinue={handlePhaseCompletionContinue}
          onDismiss={handlePhaseCompletionDismiss}
        />
      )}

      {/* Assessment Summary Modal */}
      {showAssessmentSummary && assessmentSummaryGoalId && userId && (
        <AssessmentSummaryLoader
          userId={userId}
          goalId={assessmentSummaryGoalId}
          selfTestResults={selfTestResults}
          onClose={() => {
            setShowAssessmentSummary(false);
            setAssessmentSummaryGoalId(null);
          }}
        />
      )}
    </div>
  );
};

export default DoelstellingenPage;
